// src/components/UI.js
// Shared design-system components used across all pages

import React from "react";

export const Btn = ({ children, variant = "primary", size = "md", onClick, disabled, style = {}, type = "button" }) => {
  const base = {
    cursor: "pointer", border: "none", borderRadius: 8,
    fontWeight: 600, fontFamily: "'Syne', sans-serif",
    transition: "all .2s", display: "inline-flex",
    alignItems: "center", gap: 6, ...style
  };
  const vars = {
    primary:   { background: "var(--rust)", color: "#fff", boxShadow: "0 2px 8px rgba(184,76,39,.28)" },
    secondary: { background: "var(--slate)", color: "#fff" },
    ghost:     { background: "transparent", color: "var(--slate)", border: "1.5px solid #c8c4bc" },
    danger:    { background: "var(--danger)", color: "#fff" },
    success:   { background: "var(--success)", color: "#fff" },
    warn:      { background: "var(--warn)", color: "#fff" },
  };
  const sizes = {
    xs: { padding: "4px 10px", fontSize: 11 },
    sm: { padding: "6px 13px", fontSize: 12 },
    md: { padding: "9px 18px", fontSize: 14 },
    lg: { padding: "12px 28px", fontSize: 15 },
  };
  return (
    <button type={type} disabled={disabled} onClick={onClick}
      style={{ ...base, ...vars[variant], ...sizes[size], opacity: disabled ? .5 : 1 }}>
      {children}
    </button>
  );
};

export const Tag = ({ label, color = "var(--slate)" }) => (
  <span style={{
    background: color + "1a", color, border: `1px solid ${color}40`,
    borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap"
  }}>{label}</span>
);

export const Input = ({ label, error, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    {label && <label style={{ fontSize: 12, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6 }}>{label}</label>}
    <input style={{
      padding: "9px 12px", border: `1.5px solid ${error ? "var(--danger)" : "#d4d0c8"}`,
      borderRadius: 8, fontSize: 14, background: "#fff", outline: "none", transition: "border .2s",
      fontFamily: "'DM Sans', sans-serif"
    }}
      onFocus={e => e.target.style.borderColor = "var(--rust)"}
      onBlur={e => e.target.style.borderColor = error ? "var(--danger)" : "#d4d0c8"}
      {...props} />
    {error && <span style={{ fontSize: 11, color: "var(--danger)" }}>{error}</span>}
  </div>
);

export const Select = ({ label, children, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    {label && <label style={{ fontSize: 12, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6 }}>{label}</label>}
    <select style={{ padding: "9px 12px", border: "1.5px solid #d4d0c8", borderRadius: 8, fontSize: 14, background: "#fff", outline: "none", fontFamily: "'DM Sans', sans-serif" }} {...props}>
      {children}
    </select>
  </div>
);

export const Textarea = ({ label, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    {label && <label style={{ fontSize: 12, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6 }}>{label}</label>}
    <textarea rows={3} style={{
      padding: "9px 12px", border: "1.5px solid #d4d0c8", borderRadius: 8, fontSize: 14,
      background: "#fff", outline: "none", resize: "vertical", fontFamily: "'DM Sans', sans-serif",
      transition: "border .2s"
    }}
      onFocus={e => e.target.style.borderColor = "var(--rust)"}
      onBlur={e => e.target.style.borderColor = "#d4d0c8"}
      {...props} />
  </div>
);

export const Card = ({ children, style = {} }) => (
  <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 16px rgba(13,13,13,.07)", border: "1px solid #eae6dc", padding: 24, ...style }}>
    {children}
  </div>
);

export const Modal = ({ open, onClose, title, children, width = 560 }) => {
  if (!open) return null;
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(13,13,13,.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: width, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 60px rgba(0,0,0,.22)", animation: "fadeUp .3s ease" }}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #eae6dc", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <h3 style={{ fontSize: 17, fontFamily: "'Syne', sans-serif" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 24, color: "#aaa", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

export const Avatar = ({ name = "?", size = 38, photo }) => {
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const hue = (name.charCodeAt(0) * 47 + name.charCodeAt(1) * 11) % 360;
  return photo
    ? <img src={photo} alt={name} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
    : <div style={{ width: size, height: size, borderRadius: "50%", background: `hsl(${hue},42%,52%)`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * .36, fontWeight: 700, fontFamily: "'Syne',sans-serif", flexShrink: 0 }}>{initials}</div>;
};

export const Spinner = ({ size = 20, color = "var(--rust)" }) => (
  <span style={{ display: "inline-block", width: size, height: size, border: `2.5px solid ${color}33`, borderTopColor: color, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
);

export const Alert = ({ type = "info", children }) => {
  const map = { info: ["#e8f0fe", "#1a56db", "ℹ️"], success: ["#eaf7f0", "var(--success)", "✅"], error: ["#fdf0ee", "var(--danger)", "❌"], warn: ["#fff8ee", "var(--warn)", "⚠️"] };
  const [bg, color, icon] = map[type] || map.info;
  return (
    <div style={{ background: bg, border: `1px solid ${color}44`, borderRadius: 8, padding: "10px 14px", color, fontSize: 13, display: "flex", alignItems: "flex-start", gap: 8 }}>
      <span>{icon}</span><span>{children}</span>
    </div>
  );
};

export const ProgressBar = ({ value, color = "var(--rust)", height = 6 }) => (
  <div style={{ height, background: "#e8e4db", borderRadius: height / 2, overflow: "hidden" }}>
    <div style={{ height: "100%", width: `${Math.min(100, value)}%`, background: color, borderRadius: height / 2, transition: "width .5s ease" }} />
  </div>
);

export const PageHeader = ({ title, sub, action }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
    <div>
      <h2 style={{ fontSize: 26, fontFamily: "'Syne',sans-serif", marginBottom: 4 }}>{title}</h2>
      {sub && <p style={{ color: "var(--slate-light)", fontSize: 14 }}>{sub}</p>}
    </div>
    {action}
  </div>
);
